import { handleSubmit } from "app/client/lib/formUtils";
import { sanitizeLinkUrl } from "app/client/lib/sanitizeUrl";
import * as loginCss from "app/client/ui/LoginPagesCss";
import * as oauthCss from "app/client/ui/OAuthCss";
import { SignInWithGristAPI } from "app/client/ui/SignInWithGristAPI";
import { cssInfoTooltipButton } from "app/client/ui/tooltips";
import { createUserImage } from "app/client/ui/UserImage";
import { cssMemberPrimary, cssMemberSecondary } from "app/client/ui/UserItem";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { confirmModal } from "app/client/ui2018/modals";
import { commonUrls } from "app/common/gristUrls";
import {
  OAuthClient,
  OAuthConsentPrompt,
  OAuthSelectAccountPrompt,
} from "app/common/LoginPageConfig";
import { tokens } from "app/common/ThemePrefs";

import { Disposable, dom, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-oauth-");

/**
 * {@link SelectAccountForm} options.
 */
export interface SelectAccountFormOptions {
  api: SignInWithGristAPI;
  cid: string;
  client: OAuthClient;
  prompt: OAuthSelectAccountPrompt;
}

/**
 * The form for the OAuth select account page (`/select-account`).
 */
export class SelectAccountForm extends Disposable {
  private _api = this._options.api;
  private _cid = this._options.cid;
  private _client = this._options.client;

  constructor(private _options: SelectAccountFormOptions) {
    super();
  }

  public buildDom() {
    const { client } = this._options;

    return [
      buildOAuthFormTitle({ client, variant: "select-account" }),
      oauthCss.selectAccountForms(
        this._buildSelectSessionUserForm(),
        this._buildLoginForm(),
      ),
    ];
  }

  /**
   * Builds a form to select an existing user from the Grist session.
   */
  private _buildSelectSessionUserForm() {
    const { prompt: { details: { users } } } = this._options;

    return oauthCss.selectAccountForm(
      { action: this._api.getConsentUrl(this._cid), method: "GET" },
      oauthCss.accountButtons(
        users.map((user, index) =>
          oauthCss.accountButton(
            oauthCss.account(
              createUserImage(user, "large"),
              oauthCss.accountNameAndEmail(
                cssMemberPrimary(user.name || dom("span", user.email), testId("select-account-user-name")),
                user.name ? cssMemberSecondary(user.email, testId("select-account-user-email")) : null,
              ),
            ),
            {
              type: "submit",
              name: "authuser",
              // The array index is a stable identifier for a user in the Grist session.
              //
              // If Grist sessions are ever changed in the future such that they no longer guarantee
              // that indexes remain stable (e.g. a per-user sign-out feature is implemented that
              // causes indexes for existing users in the session to change), a different identifier
              // may need to be used here to prevent a class of bugs related to unstable identifiers.
              value: String(index),
            },
            testId("select-account-user"),
          ),
        ),
      ),
    );
  }

  /**
   * Builds a form that redirects to the login page (`/login`) to sign in to a new account.
   */
  private _buildLoginForm() {
    return oauthCss.selectAccountForm(
      oauthCss.accountButtons(
        oauthCss.accountButton(
          oauthCss.accountButton.cls("-add"),
          oauthCss.account(
            createUserImage("addUser", "large"),
            oauthCss.accountNameAndEmail(cssMemberPrimary("Add account")),
          ),
          { type: "submit" },
          testId("select-account-add-user"),
        ),
      ),
      handleSubmit({
        onSubmit: () => this._api.redirectToLogin(this._cid, this._client.id),
      }),
    );
  }
}

/**
 * {@link ConsentForm} options.
 */
export interface ConsentFormOptions {
  api: SignInWithGristAPI;
  cid: string;
  client: OAuthClient;
  prompt: OAuthConsentPrompt;
}

/**
 * The form for the OAuth consent page (`/consent`).
 */
export class ConsentForm extends Disposable {
  private _api = this._options.api;
  private _cid = this._options.cid;

  constructor(private _options: ConsentFormOptions) {
    super();
  }

  public buildDom() {
    const { client } = this._options;

    return [
      buildOAuthFormTitle({ client, variant: "consent" }),
      this._buildChangeAccountForm(),
      this._buildConsentForm(),
    ];
  }

  /**
   * Builds a form that redirects to the select account page (`/oauth/interaction/:cid/select-account`)
   * to sign in as a different user.
   */
  private _buildChangeAccountForm() {
    const { prompt: { details: { user } } } = this._options;

    // Use a form for returning to the select account page for consistency
    // with the other form on this page, and to disallow opening the select
    // account page in a new tab.
    return cssChangeAccountForm(
      loginCss.flexJustifyCenter(
        oauthCss.changeAccountButton(
          oauthCss.accountSmall(
            createUserImage(user, "small"),
            oauthCss.accountNameAndEmail(cssMemberPrimary(
              dom("span", user.email, testId("consent-change-account-email"))),
            ),
            icon("Pencil"),
          ),
          testId("consent-change-account"),
        ),
      ),
      handleSubmit({
        onSubmit: () => this._api.redirectToSelectAccount(this._cid),
      }),
    );
  }

  /**
   * Builds a form that allows or denies requested scopes and redirects to the OAuth client
   * with an authorization code (if allowed) or an error (if denied).
   */
  private _buildConsentForm() {
    const { client, prompt: { details: { user } } } = this._options;
    const pending = Observable.create(null, false);

    return cssConsentForm(
      dom.autoDispose(pending),
      cssConsentFormSubheading(
        loginCss.lightlyBolded(client.name),
        " wants to access this info about you",
        testId("consent-subheading"),
      ),
      cssSharedInfo(
        cssSharedInfoRow(
          cssSharedInfoIcon("Public"),
          cssSharedInfoValueAndLabel(
            cssSharedInfoValue(user.name, testId("consent-name")),
            cssSharedInfoLabel("Name"),
          ),
        ),
        cssSharedInfoRow(
          cssSharedInfoIcon("Mail"),
          cssSharedInfoValueAndLabel(
            cssSharedInfoValue(user.email, testId("consent-email")),
            cssSharedInfoLabel("Email address"),
          ),
        ),
      ),
      cssConsentFormButtons(
        loginCss.bigBasicButton("Cancel",
          dom.prop("disabled", pending),
          cssConsentFormButton.cls(""),
          {
            type: "submit",
            name: "decision",
            value: "deny",
          },
          testId("consent-cancel"),
        ),
        loginCss.bigPrimaryButton("Authorize",
          dom.prop("disabled", pending),
          cssConsentFormButton.cls(""),
          {
            type: "submit",
            name: "decision",
            value: "allow",
          },
          testId("consent-authorize"),
        ),
      ),
      loginCss.centeredText(
        loginCss.lightText(`You'll be redirected to ${client.uri} after authorizing`),
      ),
      loginCss.centeredText(
        { style: "margin-top: 24px" },
        cssLink("Learn more about getgrist.com authentication", {
          href: commonUrls.help,
          target: "_blank",
        }),
      ),
      handleSubmit({
        pending,
        onSubmit: (_fields, _form, event) =>
          this._api.consent(
            this._cid,
            (event.submitter as HTMLButtonElement).value as "allow" | "deny",
          ),
        onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
      }),
    );
  }
}

/**
 * {@link buildOAuthFormTitle} options.
 */
export interface OAuthFormTitleOptions {
  /**
   * OAuth client information.
   */
  client: OAuthClient;
  /**
   * The title variant.
   *
   * May be one of:
   *  - "select-account": Suitable for the select account page (`/select_account`).
   *  - "login": Suitable for the login page (`/login`) to show client branding.
   *  - "consent": Suitable for the consent page (`/consent`).
   */
  variant: "select-account" | "login" | "consent";
}

/**
 * Builds the title section for an OAuth form (`/select-account`, `/consent`)
 * or the login page (`/login`).
 */
export function buildOAuthFormTitle(options: OAuthFormTitleOptions) {
  const { client, variant } = options;

  let heading: string;
  switch (variant) {
    case "select-account": {
      heading = "Select a Grist account";
      break;
    }
    case "consent": {
      heading = `Sign in to ${client.name}`;
      break;
    }
    case "login": {
      heading = `Sign in to Grist`;
      break;
    }
    default: {
      const exhaustiveCheck: never = variant;
      throw new Error(`Unhandled OAuth form title variant: ${exhaustiveCheck}`);
    }
  }

  return [
    loginCss.flexJustifyCenter(loginCss.gristLogo()),
    loginCss.centeredFormHeading(heading, testId("heading")),
    loginCss.centeredFormSubheading(
      variant !== "consent" ?
        ["to continue to ", loginCss.lightlyBolded(client.name)] :
        null,
      buildOAuthClientInfo(client),
      testId("subheading"),
    ),
  ];
}

/**
 * Builds an OAuth client URI with a tooltip that shows more details when clicked.
 */
export function buildOAuthClientInfo(client: {
  name: string;
  uri: string;
  contact: string;
}) {
  return cssOAuthClientInfo(
    loginCss.lightText(client.uri, testId("client-uri")),
    cssInfoTooltipButton("?",
      dom.on("click", () =>
        confirmModal("Grist server info", "Got it", () => {}, {
          explanation: dom("div",
            dom("p", loginCss.lightlyBolded("Name: "), dom("span", client.name, testId("client-info-name"))),
            dom("p",
              loginCss.lightlyBolded("URL: "),
              cssLinkBreakAll(client.uri, {
                href: sanitizeLinkUrl(client.uri),
                target: "_blank",
              }, testId("client-info-uri")),
            ),
            dom("p",
              loginCss.lightlyBolded("Contact: "),
              cssLinkBreakAll(client.contact, {
                href: sanitizeLinkUrl(`mailto:${client.contact}`),
              }, testId("client-info-contact")),
            ),
          ),
          hideCancel: true,
        }),
      ),
      testId("client-info"),
    ),
  );
}

const cssChangeAccountForm = styled("form", `
  margin-bottom: 24px;
`);

const cssConsentForm = styled("form", `
  width: 100%;
`);

const cssConsentFormSubheading = styled("div", `
  font-size: 16px;
  margin-bottom: 24px;
`);

const cssSharedInfo = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 48px;
  padding: 0 16px;
`);

const cssSharedInfoRow = styled("div", `
  display: flex;
  gap: 16px;
`);

const cssSharedInfoIcon = styled(icon, `
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  --icon-color: ${tokens.black};
`);

const cssSharedInfoValueAndLabel = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 4px;
`);

const cssSharedInfoValue = styled("span", `
  color: ${tokens.black};
  font-size: 15px;
  line-height: 15px;
`);

const cssSharedInfoLabel = styled("span", `
  color: ${tokens.secondary};
  font-size: 13px;
`);

const cssConsentFormButtons = styled("div", `
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  margin-top: 32px;
`);

const cssConsentFormButton = styled("button", `
  width: 50%;
`);

const cssOAuthClientInfo = styled(loginCss.flexJustifyCenter, `
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
`);

const cssLinkBreakAll = styled(cssLink, `
  word-break: break-all;
`);
